import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import hasCustomPaymentCompletePage from 'src/common/components/payment/utils/hasCustomPaymentCompletePage';
import { useRouteProps } from 'src/common/providers/RoutePropsProvider';
import { selectSubscriptionSuccessPartnerConfig } from 'src/common/selectors/config';
import { isLarge, isXLarge } from 'src/common/utils/breakpoints';
import { useBreakpoint } from './useBreakpoint';

export default function useSideBar(isMobile, query, pathname) {
  const { isLanding, hideLeft, hideRight, matchUrl, isDiscord } = useRouteProps();
  const subscriptionSuccessPartnerConfig = useSelector(selectSubscriptionSuccessPartnerConfig);
  const isLargeBreakpoint = useBreakpoint(isLarge);
  const isRightSideBreakpoint = useBreakpoint(isXLarge);
  const isNotLandingOrMobile = !(isLanding || isMobile);
  const isLeftSideDocked = isNotLandingOrMobile && !isDiscord && isLargeBreakpoint;
  // Whether the route has a right side at a wide enough viewport. Its space is reserved with a media
  // query rather than isRightSideOpen: the server guesses the viewport, so the content would
  // otherwise render at full width and narrow during hydration.
  const hasRightSide = isNotLandingOrMobile && !isDiscord && !hideRight;
  const isRightSideOpen = hasRightSide && isRightSideBreakpoint;
  const [isLeftSideOpen, setIsLeftSideOpen] = useState(isLeftSideDocked);

  const shouldHideLeftSide = hasCustomPaymentCompletePage(
    query,
    pathname,
    subscriptionSuccessPartnerConfig,
  );
  const hideLeftSide = hideLeft || shouldHideLeftSide;

  // Also reset when the viewport crosses the large breakpoint, not only when docking changes. The
  // landing page never docks, so a sidebar opened below the breakpoint would otherwise stay open
  // as a modal after the window widens.
  useEffect(() => {
    setIsLeftSideOpen(hideLeftSide ? false : isLeftSideDocked);
  }, [hideLeftSide, matchUrl, isLeftSideDocked, isLargeBreakpoint]);

  const handleLeftSideToggle = useCallback(() => {
    setIsLeftSideOpen((value) => !value);
  }, []);

  return {
    isLeftSideDocked,
    isLeftSideOpen,
    hasRightSide,
    isRightSideOpen,
    hideLeftSide,
    handleLeftSideToggle,
  };
}
